import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  readonly updateRequired$ = new Subject<void>();

  private clientBuildTime: number | null = null;
  private lastCheckedAt = 0;
  private isUpdateRequired = false;
  private readonly checkIntervalMs = 10 * 60 * 1000;

  constructor(private http: HttpClient) {}

  get updateRequired(): boolean { return this.isUpdateRequired; }

  checkVersionIfNeeded(): Observable<boolean> {
    if (this.isUpdateRequired) return of(false);

    const now = Date.now();
    if ((now - this.lastCheckedAt) < this.checkIntervalMs) return of(true);

    // Set optimistically to prevent concurrent duplicate checks.
    this.lastCheckedAt = now;

    const clientVersion$ = this.clientBuildTime !== null
      ? of(this.clientBuildTime)
      : this.http.get<{ buildTime: number }>(`/assets/version.json?v=${Date.now()}`).pipe(
          tap(v => { this.clientBuildTime = v.buildTime; }),
          map(v => v.buildTime),
          catchError(() => of(0))
        );

    return clientVersion$.pipe(
      switchMap(clientBuildTime => {
        if (clientBuildTime === 0) return of(true);

        return this.http.get<{ buildTime: number }>('/charitycentral/checkversion').pipe(
          tap(serverVersion => {
            if (serverVersion.buildTime !== 0 && serverVersion.buildTime !== clientBuildTime) {
              this.isUpdateRequired = true;
              this.updateRequired$.next();
            }
          }),
          map(() => !this.isUpdateRequired),
          catchError(() => of(true))
        );
      })
    );
  }
}
